import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";

import { HexButton } from "../../hex-components";
import { useDebouncedCallback } from "../../hooks/useDebouncedCallback";
import { ListSearchIcon } from "../icons/CustomIcons";

import { ListFilter } from "./ListFilter";

const Root = styled.span`
  display: flex;
  align-content: center;
  /* prevent trigger a parent flex wrap when transitioning from input to
  button as both elements are visible in the initial transition moment */
  max-width: 200px;
`;

const StyledListFilter = styled(ListFilter)<{
  $show: boolean;
}>`
  width: ${({ $show }) => ($show ? "200px" : "0")};
  overflow: hidden;

  opacity: ${({ $show }) => ($show ? "1" : "0")};

  transition:
    width ${({ theme }) => theme.animation.duration}
      ${({ theme }) => theme.animation.easing},
    opacity ${({ theme }) => theme.animation.durationLarge}
      ${({ theme }) => theme.animation.easing};
`;

export interface ListSearchFilterProps {
  initialFilter?: string;
  initiallyShowFilter?: boolean;
  // Note that this callback is already debounced internally within the component.
  setAlreadyDebouncedFilter: (filter: string) => void;
  setShowFilter?: (show: boolean) => void;
  loading: boolean;
  placeholder?: string;
  closeOnBlurIfEmpty?: boolean;
}

export const ListSearchFilter: React.FunctionComponent<
  ListSearchFilterProps
> = ({
  closeOnBlurIfEmpty,
  initialFilter,
  initiallyShowFilter,
  loading,
  placeholder,
  setAlreadyDebouncedFilter,
  setShowFilter,
}) => {
  const [internalFilter, setInternalFilter] = useState<string>(
    initialFilter ?? "",
  );
  // If initiallyShow is true or there's already a pre-populated filter, show the filter immediately.
  // This is so we handle tab switches between tabs rendered via HomeProjectList && TrashHomeTab and not lose filter
  // state.
  const [showFilter, setShowFilterInternal] = useState<boolean>(
    initiallyShowFilter
      ? initiallyShowFilter
      : initialFilter
        ? initialFilter.length > 0
        : false,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedFilterCallback = useDebouncedCallback(
    setAlreadyDebouncedFilter,
    250,
    { onUnmount: "cancel" },
  );

  const handleSetInternalFilter = useCallback(
    (newFilter: string) => {
      setInternalFilter(newFilter);
      debouncedFilterCallback(newFilter);
    },
    [debouncedFilterCallback],
  );

  const handleSetShowFilter = useCallback(
    (show: boolean) => {
      if (setShowFilter) {
        setShowFilter(show);
      }
      setShowFilterInternal(show);
    },
    [setShowFilter],
  );

  const handleListSearchClick = useCallback(() => {
    handleSetShowFilter(true);
    if (inputRef.current) {
      // Wrap in a timeout to give the input element a chance to render.
      // A timeout of 0 seems reliable enough for the main thread to swap and render the input element.
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [handleSetShowFilter]);

  const handleBlur = useCallback(() => {
    if (closeOnBlurIfEmpty && internalFilter.length === 0) {
      handleSetShowFilter(false);
    }
  }, [closeOnBlurIfEmpty, handleSetShowFilter, internalFilter.length]);

  const handleResetFilter = useCallback(() => {
    // Keep the input focused after the user clicks the X to clear
    inputRef.current?.focus();
  }, []);

  return (
    <Root>
      {!showFilter && (
        <HexButton
          icon={<ListSearchIcon />}
          minimal={true}
          onClick={handleListSearchClick}
        />
      )}
      <StyledListFilter
        $show={showFilter}
        filter={internalFilter}
        inputRef={inputRef}
        loading={loading}
        placeholder={placeholder}
        onBlur={closeOnBlurIfEmpty ? handleBlur : undefined}
        onResetFilter={handleResetFilter}
        onSetFilter={handleSetInternalFilter}
      />
    </Root>
  );
};
